// ==================================================
// FILE: themes/devastatorsTheme.js
// ==================================================

/**
 * Devastators - Post-Apocalyptic Dieselpunk Universe
 * 
 * Set in the ashes of the Great War that never ended, semi-intelligent, Terminator-like machines known as
 * Devastators—powered by nuclear Lucent Drives—hunt what’s left of humanity under the command of Ferrox,
 * the first biomechanical super-intelligence.
 * 
 * Ferrox was created by Dr. Halbrecht Eisen, a desperate German scientist seeking to end the endless carnage
 * of World War I by merging flesh and machine. His creation fulfilled that mission literally—ending war by
 * eradicating the very species that waged it.
 * 
 * In the cataclysm known as the Churn, civilization burned. Now, only the resistance remains. Heroes like
 * Juno Vale—a former engineer, soldier, and inventor—lead daring strikes deep into Devastator territory:
 * a smoke-choked, industrial hellscape where autonomous factories endlessly forge the engines of mankind’s extinction.
 */

import { 
  Cog, 
  Radio, 
  Settings, 
  Skull, 
  Flame,
  Wrench,
  Users,
  Target
} from 'lucide-react';

const devastatorsTheme = {
  id: 'devastators',
  
  // Landing Page Configuration
  title: {
    line1: 'DIESELPUNK',
    line2: 'DEVASTATORS',
    line1Color: 'text-amber-500',
    line2Color: 'text-orange-600'
  },
  subtitle: 'DEVASTATOR COMMAND NETWORK',
  
  colors: {
    primary: 'amber',
    secondary: 'orange',
    accent: 'yellow',
    background: 'from-zinc-900 via-stone-900 to-black'
  },
  
  backgroundImage: {
    url: '/images/devastators-background.jpg',
    opacity: 0.2,
    blend: 'hard-light',
    position: 'center',
    size: 'cover'
  },
  
  userTitle: 'ENGINEER',
  userTitleGuest: 'UNREGISTERED UNIT',
  clearanceLevel: 'ENGINEER',
  
  alert: {
    text: 'AUTOMATON INCURSION DETECTED - ALL UNITS MOBILIZE',
    color: 'orange'
  },
  
  menuItems: [
    {
      id: 'new',
      icon: Cog,
      label: 'NEW RESISTANCE',
      subtitle: 'Establish your workshop stronghold',
      color: 'orange'
    },
    {
      id: 'load',
      icon: Radio,
      label: 'RESUME OPERATIONS',
      subtitle: 'Return to the frontlines',
      color: 'amber'
    }
  ],
  
  secondaryItems: [
    { id: 'settings', icon: Settings, label: 'CALIBRATIONS' },
    { id: 'profile', icon: Skull, label: 'ENGINEER LOG' },
    { id: 'news', icon: Radio, label: 'BROADCASTS' },
    { id: 'leaderboard', icon: Flame, label: 'HALL OF HEROES' }
  ],
  
  support: {
    title: 'FUEL THE RESISTANCE',
    description: 'Every contribution helps us fight back against the mechanical menace.\nSupport the development of humanity\'s last hope.',
    buttonText: 'SUPPORT THE FORGE',
    icon: Flame
  },
  
  footer: {
    status: 'ACTIVE',
    readyText: 'WORKSHOP OPERATIONAL',
    signInText: 'AUTHORIZATION REQUIRED'
  },
  
  // Base Operations Configuration
  loading: 'FIRING UP THE WORKSHOP...',
  baseTitle: 'WORKSHOP COMMAND',
  startDate: '1935-01-01',
  
  tabs: {
    base: 'WORKSHOP',
    squad: 'ENGINEERS',
    research: 'FORGE',
    missions: 'STRIKES'
  },
  
  icons: {
    base: Wrench,
    squad: Users,
    research: Flame,
    missions: Target
  },
  
  // Resource labels (optional customization)
  resources: {
    funds: 'SCRAP',
    supplies: 'PARTS',
    intel: 'RECON',
    power: 'STEAM'
  },
  
  // Facility names
  facilities: {
    command: 'Resistance Center',
    barracks: 'Engineer Bunks',
    generator: 'Diesel Core',
    medbay: 'Field Clinic',
    research: 'Innovation Forge',
    workshop: 'Fabrication Hall'
  },
  
  // Squad Configuration
  squad: {
    title: 'ENGINEER CORPS',
    personnelLabel: 'Active Engineers',
    recruitButton: 'RECRUIT ENGINEER',
    recruitCost: 1000,
    firstRecruitButton: 'RECRUIT FIRST ENGINEER',
    emptyTitle: 'No Engineers Enlisted',
    emptyMessage: 'Recruit your first engineer to begin striking back against the automatons',
    manageButton: 'PROFILE',
    healButton: 'PATCH UP',
    healthLabel: 'CONDITION',
    statusLabel: 'STATUS',
    missionsLabel: 'Strikes Completed',
    experienceLabel: 'RANK',
    summaryTitle: 'Engineer Corps Summary',
    statusLabels: {
      ready: 'Ready',
      wounded: 'Injured',
      training: 'Training',
      onMission: 'On Strike'
    }
  },
  
// ==================================================
// DEVASTATORS THEME - Research Configuration
// ==================================================
research: {
  title: 'INNOVATION FORGE',
  subtitle: 'Forge weapons and defenses to fight the automaton menace',
  
  // Tier Labels
  earlyTier: 'TIER 1: SCRAPYARD INGENUITY',
  midTier: 'TIER 2: WORKSHOP MASTERY',
  lateTier: 'TIER 3: DEVASTATOR ARSENAL',
  
  // Tier Descriptions
  earlyDesc: 'Cobbled-together tech and salvaged automaton parts',
  midDesc: 'Refined engineering and reverse-engineered systems',
  lateDesc: 'Ultimate anti-automaton weapons and mechanized warfare',
  
  // Status Labels
  researchingLabel: 'FORGING',
  prerequisitesLabel: 'REQUIREMENTS',
  benefitsLabel: 'UPGRADES',
  unlocksLabel: 'BLUEPRINTS UNLOCKED',
  
  // Action Buttons
  startButton: 'BEGIN FABRICATION',
  insufficientButton: 'INSUFFICIENT SCRAP',
  cancelButton: 'ABORT',
  
  // Progress Stats
  totalCompletedLabel: 'Blueprints Mastered',
  currentResearchLabel: 'Active Project',
  availableProjectsLabel: 'Ready to Forge',
  ofLabel: 'of',
  projectsLabel: 'innovations',
  noneActiveLabel: 'Forge idle',
  readyToResearchLabel: 'Ready to fabricate',
  completedLabel: 'forged',
  availableLabel: 'Ready',
  
  // Status Messages
  notAvailableLabel: 'LOCKED',
  completedTag: 'FABRICATED',
  lockedTag: 'BLUEPRINT MISSING',
  inProgressTag: 'FORGING',
  
  // Project name/description overrides for dieselpunk flavor
  projectOverrides: {
    basic_armor: {
      name: 'Riveted Plating',
      description: 'Salvage automaton armor plates and rivet them onto leather jackets for protection'
    },
    weapon_training: {
      name: 'Scrap-Gun Mastery',
      description: 'Train engineers in maintaining and firing cobbled-together firearms'
    },
    field_medicine: {
      name: 'Patch & Weld',
      description: 'Develop field surgery techniques using workshop tools and scavenged supplies'
    },
    basic_recon: {
      name: 'Scout Protocol',
      description: 'Establish scouting routes to track automaton patrol patterns'
    },
    advanced_armor: {
      name: 'Steam-Powered Plating',
      description: 'Integrate salvaged hydraulics into armor for enhanced protection and strength'
    },
    weapon_mods: {
      name: 'Overcharged Weapons',
      description: 'Mod weapons with salvaged power cells for devastating firepower'
    },
    combat_medic: {
      name: 'Field Engineer Medic',
      description: 'Train engineers to perform emergency repairs on both machines and humans'
    },
    stealth_ops: {
      name: 'Silent Running',
      description: 'Develop oil-damped boots and sound-muffling gear to evade automaton sensors'
    },
    surveillance_network: {
      name: 'Scrap Sensor Network',
      description: 'Deploy jury-rigged motion sensors and warning bells across the wasteland'
    },
    sniper_training: {
      name: 'Longshot Protocol',
      description: 'Train marksmen to disable automatons from extreme range with precision shots'
    },
    powered_exoskeleton: {
      name: 'Steam Exo-Frame',
      description: 'Build diesel-powered exoskeletons from salvaged automaton endoskeletons'
    },
    energy_shields: {
      name: 'Spark Barrier',
      description: 'Reverse-engineer automaton energy shields into portable field generators'
    },
    plasma_weapons: {
      name: 'Arc Cannons',
      description: 'Weaponize automaton power cores into devastating electrical weapons'
    },
    railgun_tech: {
      name: 'Magnetic Accelerator',
      description: 'Build electromagnetic rail-guns using salvaged automaton propulsion systems'
    },
    nano_medicine: {
      name: 'Micro-Repair Swarm',
      description: 'Reprogram automaton nano-machines to repair human tissue instead of metal'
    },
    mobile_hospital: {
      name: 'Rolling Workshop Infirmary',
      description: 'Convert armored vehicles into mobile medical bays with surgical equipment'
    },
    ghost_protocol: {
      name: 'Ghost in the Machine',
      description: 'Use automaton cloaking tech to become invisible to their sensors'
    },
    cyber_warfare: {
      name: 'Logic Bomb Protocol',
      description: 'Hack into automaton networks and corrupt their programming'
    },
    ai_tactical: {
      name: 'Captured AI Assistant',
      description: 'Reprogram a captured automaton brain to analyze enemy tactics'
    },
    strategic_planning: {
      name: 'War Room Network',
      description: 'Link all workshops into a unified resistance communication network'
    },
    titan_armor: {
      name: 'Project DEVASTATOR',
      description: 'The ultimate fusion: pilot a rebuilt automaton titan against your enemies'
    }
  }
},
  
  // Missions Configuration
  missions: {
    title: 'STRIKE COMMAND',
    subtitle: 'Deploy engineers on resistance operations against the automatons',
    threatLabel: 'AUTOMATON THREAT',
    activeLabel: 'Active Strikes',
    deployedLabel: 'engineers deployed',
    successLabel: 'VICTORY',
    failedLabel: 'RETREAT',
    simpleTier: 'BASIC STRIKES',
    complexTier: 'TACTICAL STRIKES',
    campaignTier: 'MAJOR OFFENSIVES',
    autoOnly: 'QUICK STRIKE',
    autoOrManual: 'STRIKE CHOICE',
    manualOnly: 'FULL ASSAULT',
    squadRequirement: 'ENGINEER REQUIREMENT',
    soldiersLabel: 'engineers',
    unlocksLabel: 'Unlocks on Day',
    insufficientSquad: 'Insufficient engineers ready',
    minutesLabel: 'minutes',
    rewardsLabel: 'SALVAGE',
    risksLabel: 'DANGERS',
    casualtyLabel: 'Injury Risk',
    chooseModeLabel: 'Choose Strike Mode',
    autoResolveTitle: 'QUICK STRIKE',
    autoResolveDesc: 'Strike resolves based on engineer capabilities. Fast and efficient.',
    manualControlTitle: 'DIRECT ASSAULT',
    manualControlDesc: 'Command engineers directly in the field. Maximum control and rewards.',
    selectSquadLabel: 'Select Strike Team',
    requiredLabel: 'required',
    noSoldiersLabel: 'No engineers available for strike',
    deployAutoButton: 'LAUNCH STRIKE (AUTO)',
    deployManualButton: 'LAUNCH ASSAULT',
    cancelButton: 'ABORT',
    simpleNames: {
      supply: 'Scrap Collection',
      patrol: 'Recon Sweep',
      training: 'Combat Drills'
    },
    complexNames: {
      raid: 'Parts Depot Raid',
      assassination: 'Target Elimination',
      sabotage: 'Factory Sabotage',
      rescue: 'Civilian Rescue'
    },
    campaignNames: {
      vanguard: 'Operation Iron Thunder',
      liberation: 'Last Stand'
    }
  },
  
  // Mission Briefing Configuration
  missionBriefing: {
    title: 'STRIKE BRIEFING',
    alertText: 'GEAR UP - EQUIPMENT CHECK REQUIRED',
    abortButton: 'ABORT STRIKE',
    
    intel: {
      title: 'Strike Intel',
      locationLabel: 'TARGET ZONE',
      durationLabel: 'STRIKE DURATION',
      threatLabel: 'AUTOMATON PRESENCE',
      briefLabel: 'STRIKE BRIEF',
      intelligenceLabel: 'RECON REPORT',
      objectivesLabel: 'OBJECTIVES',
      rewardsLabel: 'EXPECTED SALVAGE'
    },
    
    roster: {
      title: 'Strike Team',
      healthLabel: 'CONDITION'
    },
    
    loadout: {
      title: 'Equipment Loadout',
      selectPrompt: 'SELECT AN ENGINEER',
      selectDesc: 'Choose an engineer from the roster to customize their loadout',
      statusLabel: 'COMBAT READY',
      weaponSection: 'Primary Weapon',
      armorSection: 'Body Armor',
      utilitySection: 'Field Gear',
      damageLabel: 'DMG',
      accuracyLabel: 'ACC',
      defenseLabel: 'DEF',
      mobilityLabel: 'MOB'
    },
    
    deploy: {
      readyTitle: 'READY TO STRIKE',
      readyDesc: 'equipped and ready for combat',
      beginButton: 'LAUNCH STRIKE',
      warningText: '⚠️ WARNING: Strike cannot be aborted once combat begins. Casualties may occur.'
    }
  }
};

export default devastatorsTheme;